import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip'
import { isValidElement, type ReactNode } from 'react'
//import { useStringGetter, useURLConfigs } from '@/hooks'
import { BiInfoCircle } from 'react-icons/bi'
import styled from 'styled-components'
import { Link } from '@/components/orders/Link'
import { tooltipStrings } from '@/constants/tooltips'

import { layoutMixins } from '@/styles/layoutMixins'

type ElementProps = {
  tooltip?: keyof typeof tooltipStrings
  tooltipString?: string
  stringParams?: Record<string, string>
  withIcon?: boolean
  children?: ReactNode
  slotTooltip?: ReactNode
  asChild?: boolean
}

type StyleProps = {
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  cursor?: 'help' | 'pointer'
  className?: string
  zIndex?: number
  noTextDecoration?: boolean
  useInlineRow?: boolean
}

const DEFAULT_TOOLTIP_Z_INDEX = 1901

export const WithTooltip = ({
  tooltip,
  tooltipString,
  stringParams,
  withIcon,
  children,
  align,
  side,
  cursor,
  className,
  zIndex,
  slotTooltip,
  noTextDecoration,
  useInlineRow = true,
  asChild = false,
}: ElementProps & StyleProps) => {
  // const stringGetter = useStringGetter()
  // const urlConfigs = useURLConfigs()

  const getTooltipStrings = tooltip && tooltipStrings[tooltip]
  if (!getTooltipStrings && !tooltipString && !slotTooltip) return <>{children}</>

  let tooltipTitle
  let tooltipBody
  let tooltipLearnMore

  if (getTooltipStrings) {
    const { title, body, learnMoreLink } = getTooltipStrings({})
    tooltipTitle = title
    tooltipBody = body
    tooltipLearnMore = learnMoreLink
  } else {
    tooltipBody = tooltipString
  }

  const hasCustomContent = slotTooltip !== undefined && slotTooltip !== null

  const defaultTooltip = (
    <dl>
      {tooltipTitle && <dt>{tooltipTitle}</dt>}
      {tooltipBody && <dd>{tooltipBody}</dd>}
      {tooltipLearnMore && (
        <dd>
          <Styled.LearnMore href={tooltipLearnMore}>
            {/*{stringGetter({ key: STRING_KEYS.LEARN_MORE })} →*/}
            Learn More →
          </Styled.LearnMore>
        </dd>
      )}
    </dl>
  )

  const tooltipContentNode = hasCustomContent ? <div>{slotTooltip}</div> : defaultTooltip

  const tooltipTriggerNode =
    asChild && isValidElement(children) ? (
      children
    ) : (
      <Styled.Abbr
        $cursor={cursor}
        $noTextDecoration={noTextDecoration}
        $useInlineRow={useInlineRow}
        tabIndex={0}
      >
        {children}
        {withIcon && <BiInfoCircle size={14} className="text-primary" />}
      </Styled.Abbr>
    )

  return (
    <Provider>
      <Root delayDuration={100} disableHoverableContent>
        <Trigger asChild>{tooltipTriggerNode}</Trigger>

        <Portal>
          <Styled.Content
            sideOffset={8}
            side={side}
            align={align}
            className={className}
            $zIndex={zIndex}
          >
            {tooltipContentNode}
            <Styled.Arrow />
          </Styled.Content>
        </Portal>
      </Root>
    </Provider>
  )
}

const Styled = {
  Abbr: styled.abbr<{
    $cursor?: StyleProps['cursor']
    $noTextDecoration?: StyleProps['noTextDecoration']
    $useInlineRow?: StyleProps['useInlineRow']
  }>`
    ${({ $useInlineRow }) => $useInlineRow !== false && layoutMixins.inlineRow}

    text-underline-position: under;
    text-decoration-color: var(--color-text-0);
    text-decoration-skip-ink: all;
    text-decoration: ${({ $noTextDecoration }) =>
      $noTextDecoration ? 'none' : 'underline dashed 0px'};

    cursor: ${({ $cursor }) => $cursor || 'help'};
  `,
  Content: styled(Content)<{ $zIndex?: number }>`
    --tooltip-surface: #ffffff;
    --tooltip-border: #e2e8f0;
    --tooltip-text: #0f172a;
    --tooltip-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.08),
      0 2px 4px -2px rgba(15, 23, 42, 0.06);
    --tooltip-radius: 0.375rem;
    --tooltip-translate-x: 0px;
    --tooltip-translate-y: -8px;
    --tooltip-scale: 0.98;

    z-index: ${({ $zIndex }) => $zIndex ?? DEFAULT_TOOLTIP_Z_INDEX};
    max-width: 18rem;
    width: max-content;

    display: grid;
    gap: 0.5rem;

    padding: 1rem;
    border-radius: var(--tooltip-radius);
    border: 1px solid var(--tooltip-border);
    background-color: var(--tooltip-surface);
    color: var(--tooltip-text);
    box-shadow: var(--tooltip-shadow);
    outline: none;

    font-size: 0.9375rem;
    line-height: 1.45;

    opacity: 0;
    transform: translate3d(var(--tooltip-translate-x), var(--tooltip-translate-y), 0)
      scale(var(--tooltip-scale));
    transition: opacity 120ms ease, transform 180ms ease;

    &[data-state='delayed-open'],
    &[data-state='instant-open'] {
      opacity: 1;
      transform: translate3d(0, 0, 0) scale(1);
    }

    &[data-state='closed'] {
      pointer-events: none;
    }

    &[data-side='top'] {
      --tooltip-translate-y: 8px;
      transform-origin: bottom center;
    }

    &[data-side='bottom'] {
      --tooltip-translate-y: -8px;
      transform-origin: top center;
    }

    &[data-side='left'] {
      --tooltip-translate-x: 8px;
      transform-origin: right center;
    }

    &[data-side='right'] {
      --tooltip-translate-x: -8px;
      transform-origin: left center;
    }

    dl {
      margin: 0;
      display: grid;
      gap: 0.35rem;
    }

    dt {
      margin: 0;
      font-weight: 600;
      font-size: 1rem;
      color: var(--tooltip-text);
    }

    dd {
      margin: 0;
      color: var(--tooltip-text);
      white-space: pre-line;
    }

    .dark &,
    body.dark & {
      --tooltip-surface: #020617;
      --tooltip-border: #1e293b;
      --tooltip-text: #e2e8f0;
      --tooltip-shadow: 0 12px 16px -4px rgba(0, 0, 0, 0.45),
        0 4px 6px -4px rgba(0, 0, 0, 0.35);
    }
  `,
  Arrow: styled(Arrow)`
    width: 0.75rem;
    height: 0.375rem;

    polygon {
      fill: var(--tooltip-surface);
    }
  `,
  LearnMore: styled(Link)`
    --link-color: var(--color-accent);
  `,
}

// Styled.Icon = styled(Icon)`
//   color: var(--color-text-0);
// `
